/**
 * DirectoryFreshStrip.jsx
 * A small "most-recently-reviewed suburbs this month" strip that makes the
 * directory feel alive. Fetches /api/directory/fresh-leaderboard and renders
 * suburb chips (with fresh-review counts) that deep-link into the SSR
 * "freshest first" hubs. Self-contained inline styles so it sits cleanly on
 * both the dark /directory/au landing and the cream owner sales home.
 *
 * variant="dark"  → /directory/au   ·  variant="light" → sales home
 */
import { useEffect, useState } from "react";

const PULSE_CSS = `
@keyframes dfsPulse{0%{transform:scale(1);opacity:.9}70%{transform:scale(2.6);opacity:0}100%{opacity:0}}
.dfs-chip{transition:transform .15s ease,box-shadow .15s ease,border-color .15s ease}
.dfs-chip:hover{transform:translateY(-2px)}
`;

export default function DirectoryFreshStrip({ variant = "light" }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    const API =
      process.env.REACT_APP_BACKEND_URL ||
      (typeof window !== "undefined" ? window.location.origin : "");
    fetch(`${API}/api/directory/fresh-leaderboard?limit=8`)
      .then((r) => r.json())
      .then(setD)
      .catch(() => {});
  }, []);

  if (!d || !Array.isArray(d.suburbs) || d.suburbs.length === 0) return null;

  const dark = variant === "dark";
  const accent = dark ? "#5cc8f5" : "#0f766e";
  const cardBg = dark ? "rgba(9,17,30,0.60)" : "#ffffff";
  const cardBorder = dark ? "rgba(92,200,245,.22)" : "#e7e1cf";
  const nameColor = dark ? "#eef4fb" : "#1a1408";
  const subColor = dark ? "#b6c2d0" : "#6b5a2a";
  const chipBg = dark ? "rgba(92,200,245,.15)" : "rgba(15,118,110,.06)";
  const chipBorder = dark ? "rgba(92,200,245,.42)" : "rgba(15,118,110,.22)";
  const countBg = dark ? "rgba(92,200,245,.28)" : "rgba(15,118,110,.14)";
  const countColor = dark ? "#071826" : accent;

  return (
    <section
      data-testid="directory-fresh-strip"
      style={{
        margin: dark ? "6px auto 0" : "40px auto 0",
        maxWidth: dark ? 1120 : "100%",
        padding: dark ? "0 20px" : 0,
      }}
    >
      <style>{PULSE_CSS}</style>
      <div
        style={{
          background: cardBg,
          border: `1px solid ${cardBorder}`,
          borderRadius: 18,
          padding: "18px 20px",
          backdropFilter: dark ? "blur(8px)" : "none",
          WebkitBackdropFilter: dark ? "blur(8px)" : "none",
          boxShadow: dark ? "0 10px 34px rgba(0,0,0,.28)" : "0 10px 30px rgba(26,20,8,.06)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 14,
            flexWrap: "wrap",
          }}
        >
          <span style={{ position: "relative", width: 10, height: 10, flex: "0 0 auto" }}>
            <span
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background: accent,
                animation: "dfsPulse 1.8s ease-out infinite",
              }}
            />
            <span
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                background: accent,
              }}
            />
          </span>
          <span
            style={{
              fontWeight: 800,
              fontSize: 14,
              letterSpacing: ".04em",
              color: nameColor,
              textTransform: "uppercase",
            }}
          >
            Rated this month
          </span>
          <span style={{ fontSize: 13, color: subColor }}>
            {Number(d.total_fresh || 0).toLocaleString()} fresh reviews across these
            suburbs · {d.month}
          </span>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {d.suburbs.map((s) => (
            <a
              key={`${s.suburb}-${s.postcode}`}
              href={s.url}
              className="dfs-chip"
              data-testid={`fresh-chip-${s.postcode}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px 8px 14px",
                borderRadius: 999,
                background: chipBg,
                border: `1px solid ${chipBorder}`,
                textDecoration: "none",
                color: nameColor,
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              <span>{s.suburb}</span>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: countColor,
                  background: countBg,
                  borderRadius: 999,
                  padding: "2px 8px",
                }}
              >
                {s.count}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
